import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import {
  Box,
  HStack,
  PrimaryButton,
  SecondaryButton,
  Switch,
  TertiaryButton,
  fontSizes,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowDownToBracketRegular,
  FaArrowRotateRightRegular,
  FaArrowUpFromBracketRegular,
  FaClockRotateLeftRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { iconSize } from '~/constants/iconSize'
import { useCsvImportLink } from '~/hooks/useCsvImportReturn'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import type { CsvDownloadState } from '~/pages/Supply48Data/models/csvDownloadState'
import { routePaths } from '~/routes/routePaths'

type LabeledSwitchProps = {
  checked: boolean
  onCheckedChange: (details: { checked: boolean }) => void
  label: string
}

/**
 * Switch + label の組み合わせ。
 *
 * Switch に固定 id を渡してはならない: SideMenuLayout はレスポンシブ用に
 * ページ内容を 2 回 (モバイル + デスクトップ) マウントするため、固定 id は
 * 重複 id になり、ark の label `for` が常に先勝ちの hidden 側 input に解決され
 * 可視側 Switch のクリックが no-op になる (実機で確認済み)。
 * auto-id + aria-label の組み合わせ (AdminBidLimits の確立パターン) を使う。
 */
const LabeledSwitch = ({
  checked,
  onCheckedChange,
  label,
}: LabeledSwitchProps): React.ReactElement => (
  <HStack align="center" gap={2}>
    <Switch
      aria-label={label}
      checked={checked}
      onCheckedChange={onCheckedChange}
      size="sm"
    />
    <span style={{ fontSize: fontSizes.md.value }}>{label}</span>
  </HStack>
)

type Props = {
  showChart: boolean
  onShowChartChange: (checked: boolean) => void
  hasDirtyRows: boolean
  isSaving: boolean
  /**
   * workflow-back-bar-readonly AC-18: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき保存・元に戻す・破棄ボタンを disabled にする（表示切替系ではなく編集・確定操作のため）。
   */
  isReadonly?: boolean
  /**
   * AC-S4D-008: 表示中の全供給源を合計した手動変更コマ数。
   * 0 件のとき全体リセットは操作対象が無いので表示しない。
   */
  manualEditCount: number
  onRevertAll: () => void
  onSave: () => void
  /**
   * セル編集の undo / redo (Ctrl+Z / Ctrl+Y と同じ操作)。
   * 出典は design/supply/48data.html の `#s48-undo` / `#s48-redo`。
   * spec (supply-48-data.md) の AC-S4D-003 はセル選択・インライン編集・Ctrl+C/V・
   * Ctrl+Enter・Delete/Backspace までで、元に戻す / やり直すの AC は未記載。
   */
  onUndo: () => void
  onRedo: () => void
  /** CSVダウンロード確認モーダルを開く (AC-S4D-007)。 */
  onCsvDownload: () => void
  /**
   * CSV ダウンロードの可否と、押せない場合の理由
   * ([[resolveCsvDownloadState]])。 未取得を空配列にフォールバックすると
   * **実データがあるのにヘッダーのみの CSV** が落ちるため、 取得中 / 取得失敗の
   * 間は押させない。 理由は tooltip で伝える (2026-09-14 デザイナー指摘
   * 「できない場合、ボタンを disable にして理由を tooltip で表示」)。
   */
  csvDownloadState: CsvDownloadState
  canUndo: boolean
  canRedo: boolean
}

/**
 * §10: ツールバーコンポーネント。
 * Switch×1 (グラフ表示) + ボタン群。
 *
 * AC-S4D-003 (#4340) で編集モードトグルを廃止したため、undo / redo / 破棄 /
 * 保存は常時表示する (需要想定画面と同じく「常時直接編集 + 保存ボタン」)。
 * 表示単位トグル (AC-S4D-037) は design/supply/48data.html 準拠でフィルタ
 * サイドバー側に置く (FilterSidebar)。
 *
 * 「手動変更のみ表示」フィルタは Phase 1 スコープ外として撤去した (#4338、
 * 2026-08-04 判断)。手動変更されたセルは AC-S4D-026 の視覚区別で識別でき、
 * 元へ戻す手段は AC-S4D-008 のリセット操作 (全体 / 列単位) が担う。
 */
/**
 * ツールバーの操作グループを区切る縦罫 (design `supply/48data.html` の
 * `<span style="width:1px;background:var(--border-low);height:24px;">`)。
 *
 * 8080 は 保存 の左右に 1 本ずつ置いて「編集の取り消し系 / 保存 / CSV 入出力」 の
 * 3 群に分けている。 装飾ではなく群の境界を示す構造なので読み上げからは外す。
 */
const ToolbarDivider = (): React.ReactElement => (
  <Box
    flexShrink={0}
    width="1px"
    height="24px"
    background="Border.lowEmphasis"
    aria-hidden="true"
  />
)

export const Supply48DataToolbar = ({
  showChart,
  onShowChartChange,
  hasDirtyRows,
  isSaving,
  isReadonly = false,
  manualEditCount,
  onRevertAll,
  onSave,
  onUndo,
  onRedo,
  onCsvDownload,
  csvDownloadState,
  canUndo,
  canRedo,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // AC-WFW-014: 読み取り専用で非活性にした編集・確定操作の理由 (tooltip)
  const { readonlyReason } = useWorkflowLockReasons()
  // 取り込んだらこの画面 (今の絞り込みのまま) へ戻る
  const csvImportLink = useCsvImportLink(
    routePaths.adminUploadsSupply48Data.path,
  )

  // **押せる時は Tooltip を付けない。** frontend の規約
  // 「可視ラベルがある button には Tooltip を重ねない」 に従い、 説明は
  // **無効時の理由だけ**に限定する (同じ Toolbar の undo / redo も可視ラベルに
  // Tooltip を重ねていない)。 `MasterTableActionBar` の CSV ダウンロードが同じ形で、
  // 押せない時だけ理由を出す。
  //
  // 無効理由は DS Tooltip だけで出す。 押せないボタンに native title を載せない
  // (UI 辞書 action-reason.md 区分 2「native title は使わない」)。
  const csvDownloadReason =
    csvDownloadState === 'active'
      ? undefined
      : t(`page:supply48Data.toolbar.csvDownloadTooltip.${csvDownloadState}`)

  return (
    /*
     * 器 (地 + 枠 + 角丸 + 内余白) を持つ。 出典は `supply/48data.html` の inline
     * `<style>` による `.page-toolbar` 上書き (padding 12px / Surface.primary /
     * 1px Border.lowEmphasis / radius 8px)。 値と prop の並びは同じ役割の
     * `components/common/filters/FilterToolbar.tsx` (フィルタ行の骨格) に合わせる。
     *
     * 経緯: 一度この器を撤去したが、 撤去の根拠にしたデザイナー指摘
     * 2026-08-28「余白が他の画面と違う」 は**画面の外周 padding** (本画面だけ
     * 32px。 `Supply48Data/index.tsx` で二重がけしていた) を指しており、
     * ツールバーの内側ではなかった。 外周を直した上でデザイナー判断
     * (2026-08-28) により器を戻す。 design 側 48data.html の上書きも同時に復帰。
     *
     * border は longhand (`borderWidth` + `borderColor`) で書く。 色なしショート
     * ハンドは `currentColor` が効いて黒く出る (PR #6516 で全 86 箇所を longhand 化)。
     */
    <HStack
      align="center"
      flexWrap="wrap"
      gap={2}
      marginBottom={2}
      padding={3}
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
    >
      {/* 左側: グラフ表示 */}
      <HStack align="center" flexWrap="wrap" flex={1} gap={4}>
        <LabeledSwitch
          checked={showChart}
          label={t('page:supply48Data.chart.toggleLabel')}
          onCheckedChange={(details) => onShowChartChange(details.checked)}
        />
      </HStack>

      {/* 右側: 操作ボタン群 */}
      <HStack align="center" gap={2}>
        {/*
          undo / redo (design `supply/48data.html` の `#s48-undo` / `#s48-redo`)。
          グリッドに focus がある間は Ctrl+Z / Ctrl+Y でも同じ操作ができる。

          design どおり**ラベル付き**にする。 以前は「design 側は CSV 導線・破棄を
          「…」 メニューへ畳んでいるのにこの画面は並べているのでラベル付きだと
          2 行に折り返す (58px → 116px)」 として icon-only にしていたが、 前提が
          2 つとも消えている: design は 2026-08-27 に CSV 2 件をツールバー直置きへ
          戻し、 レイアウトも右グループ側で吸収する形に変わった。
          Chromium 実測で 1600 / 1440 / 1280 / 1150px のいずれもラベル付きで
          折り返さない (高さ 72px のまま。 2026-08-28)。

          可視ラベルを持つので Tooltip は付けない。 ショートカットの補足だけ
          native `title` に残す (design の `title="元に戻す (Ctrl+Z)"` と同じ)。
        */}
        <ButtonTooltip content={readonlyReason}>
          <SecondaryButton
            disabled={!canUndo || isReadonly}
            onClick={onUndo}
            size="sm"
            title={t('page:supply48Data.toolbar.undoTitle')}
          >
            <FaClockRotateLeftRegular />
            {t('page:supply48Data.toolbar.undo')}
          </SecondaryButton>
        </ButtonTooltip>
        <ButtonTooltip content={readonlyReason}>
          <SecondaryButton
            disabled={!canRedo || isReadonly}
            onClick={onRedo}
            size="sm"
            title={t('page:supply48Data.toolbar.redoTitle')}
          >
            <FaArrowRotateRightRegular />
            {t('page:supply48Data.toolbar.redo')}
          </SecondaryButton>
        </ButtonTooltip>
        {/* AC-S4D-008: 手動変更が 1 件以上ある場合のみリセット操作を提供する。
            文言は spec AC-S4D-008 と design `supply/48data.html` に合わせて
            「自動計算に戻す (N)」。 押す前に戻る規模が分かるように件数を載せる
            (2026-08-25。 以前は「全体を自動計算値に戻す」 で件数はダイアログ本文のみだった)。 */}
        {manualEditCount > 0 && (
          <ButtonTooltip content={readonlyReason}>
            <SecondaryButton
              disabled={isReadonly}
              onClick={onRevertAll}
              size="sm"
            >
              {t('page:supply48Data.toolbar.revertAllToAuto', {
                count: manualEditCount,
              })}
            </SecondaryButton>
          </ButtonTooltip>
        )}
        <ToolbarDivider />
        <ButtonTooltip content={readonlyReason}>
          <PrimaryButton
            disabled={!hasDirtyRows || isReadonly}
            loading={isSaving}
            onClick={onSave}
            size="sm"
          >
            {t('page:supply48Data.toolbar.save')}
          </PrimaryButton>
        </ButtonTooltip>
        <ToolbarDivider />

        {/*
          CSV 入出力は本画面で処理せず、データ管理画面へ遷移する
          (spec supply/supply-48-data.md AC-S4D-007 / AC-S4D-007-2、
          uploads/common.md AC-DUC-027)。
          - アップロード: 48点データの個別アップロード画面 (ウィザード Step1) を
            直接開く (AC-S4D-007-2「48点データのアップロードフロー」/ AC-DUC-027
            「該当データ種別の個別アップロード画面」)。
          - ダウンロード: **本画面で完結する** (AC-S4D-007、 issue #6382)。
            確認モーダルで 適用中のフィルタ条件・該当件数・基準時点 を出してから
            出力する。 旧実装は共通データ取込画面へ遷移していたが、 遷移先は
            uploads レジストリの全量を出すだけで画面のフィルタを 1 つも
            引き継げず、 起票の「いつの期間を出力するのか分からない」
            「対象外の項目が出る」 に当たる状態だった。
        */}
        <ButtonTooltip content={csvDownloadReason}>
          <TertiaryButton
            disabled={csvDownloadState !== 'active'}
            onClick={onCsvDownload}
            size="sm"
          >
            <FaArrowDownToBracketRegular boxSize={iconSize.labeledButton} />
            {t('page:supply48Data.toolbar.csvDownload')}
          </TertiaryButton>
        </ButtonTooltip>
        <TertiaryButton asChild size="sm">
          <Link to={csvImportLink}>
            <FaArrowUpFromBracketRegular boxSize={iconSize.labeledButton} />
            {t('page:supply48Data.toolbar.csvUpload')}
          </Link>
        </TertiaryButton>
      </HStack>
    </HStack>
  )
}
